/**
 *
 * AuthenticationImage
 *
 */

import React, { useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import _classNames from 'classnames';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeSelectClientLanding } from 'containers/ClientLanding/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import useSiteCopySelector from 'components/useSiteCopySelector';
import { getLogos, getLogoComponent } from 'components/NavBarMenu/utils';
import { getImageFile, replacePlaceholders } from 'utils/stringUtils';

const useStyles = makeStyles(theme => ({
  wrapper: {
    width: '100%',
    height: '100%',
    position: 'relative',
    margin: '0 auto',
  },
  fixedWrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
  },
  fixed: {
    position: 'fixed',
  },
  container: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '24px 48px',
    backgroundImage: props => `url("${props.image}")`,
    backgroundSize: '128%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center bottom',
    [theme.breakpoints.down('1320')]: {
      paddingLeft: 36,
      paddingRight: 36,
    },
  },
  topContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '100%',
  },
  separateClientLogo: {
    height: 22,
  },
  clientLogo: {
    height: 51,
  },
  stackedClientLogo: {
    height: 32,
    marginTop: 20,
  },
  textColor: {
    color: theme.palette.text.primary,
  },
  title: {
    marginBottom: '1.3em',
    maxWidth: 440,
    [theme.breakpoints.down('1260')]: {
      ...theme.typography.h4,
    },
  },
  benefitIconWrapper: {
    display: 'flex',
    width: '100%',
    justifyContent: 'center',
  },
  benefitIcon: {
    height: 82,
    width: 'auto',
    marginBottom: 12,
  },
  footer: {
    width: '100%',
    ...theme.typography.pxSmall,
    textAlign: 'left',
  },
  logoWrapper: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 20,
  },
  clientLogoWrapper: {
    marginBottom: 96,
  },
  stackedLogoWrapper: {
    flexDirection: 'column',
    width: '100%',
  },
  logoRoot: {
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    marginRight: 16,
  },
  logo: {
    maxHeight: 70,
  },
  stackedLogoRoot: {
    marginRight: 0,
  },
}));

function AuthenticationImage({ isSignUp }) {
  const ref = useRef(null);
  const [dimensions, setDimensions] = useState([0, 0]);
  const [authImageSiteCopy] = useSiteCopySelector([
    'authentication-pages-image',
  ]);
  const clientDetails = useSelector(makeSelectClientDetails());
  const clientLanding = useSelector(makeSelectClientLanding());
  const classes = useStyles({
    image: getImageFile(authImageSiteCopy),
  });

  useLayoutEffect(() => {
    const headerEl = document.getElementsByTagName('header');
    setDimensions([ref.current.offsetWidth, headerEl[0].offsetHeight]);
  }, []);

  const isSeparateLogoLayout = _get(
    clientDetails,
    'metadata.separateLogoLayout',
    false,
  );
  const { logos, logosUrl } = getLogos(clientDetails, clientLanding);
  const firstLogo = logos[0];
  const firstLogoUrl = logosUrl[0];
  const secondLogo = logos[1];
  const secondLogoUrl = logosUrl[1];

  const hasClient = !_isEmpty(clientDetails);
  const isStackedLayout = _get(
    clientDetails,
    'metadata.authStackedLayout',
    false,
  );

  const header = _get(
    authImageSiteCopy,
    ['pageCopy', 'customHeader', _get(clientDetails, 'shortName')],
    _get(authImageSiteCopy, 'pageHeader', ''),
  );

  const FirstLogoComponent = getLogoComponent({
    url: firstLogoUrl,
    image: (
      <img
        src={firstLogo}
        alt="CredibleMind"
        className={_classNames(classes.logoRoot, classes.logo, {
          [classes.stackedLogoRoot]: isStackedLayout,
        })}
      />
    ),
    shouldRedirectOnClientUrl: _get(
      clientDetails,
      'metadata.shouldRedirectOnClientUrl',
    ),
  });
  const SecondLogoComponent = getLogoComponent({
    url: secondLogoUrl,
    image: (
      <img
        src={secondLogo}
        alt={clientDetails.name}
        className={
          isSeparateLogoLayout
            ? classes.separateClientLogo
            : _classNames(classes.clientLogo, {
                [classes.stackedClientLogo]: isStackedLayout,
              })
        }
      />
    ),
    shouldRedirectOnClientUrl: _get(
      clientDetails,
      'metadata.shouldRedirectOnClientUrl',
    ),
  });

  return (
    <Grid item md={5} style={{ height: `calc(100vh - ${dimensions[1]}px` }}>
      <div className={classes.wrapper}>
        <div className={classes.fixedWrapper} ref={ref}>
          <div
            className={classes.fixed}
            style={{
              width: dimensions[0],
              height: `calc(100vh - ${dimensions[1]}px`,
            }}
          >
            <div className={classes.container}>
              <div className={classes.topContainer}>
                <div
                  className={_classNames(classes.logoWrapper, {
                    [classes.clientLogoWrapper]: hasClient,
                    [classes.stackedLogoWrapper]: isStackedLayout,
                  })}
                >
                  {FirstLogoComponent}
                  {secondLogo && SecondLogoComponent}
                </div>
                <Typography
                  variant="h3"
                  align="center"
                  className={_classNames(classes.textColor, classes.title)}
                >
                  {header}
                </Typography>
                {isSignUp && (
                  <Grid container spacing={2}>
                    {_get(authImageSiteCopy, 'pageCopy.items', []).map(item => (
                      <Grid item md={4}>
                        <div className={classes.benefitIconWrapper}>
                          <img
                            src={item.icon}
                            className={classes.benefitIcon}
                            alt=""
                          />
                        </div>
                        <Typography
                          component="p"
                          className={classes.textColor}
                          variant="overline"
                          align="center"
                        >
                          {item.label}
                        </Typography>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </div>
              <Typography
                className={_classNames(classes.textColor, classes.footer)}
              >
                {replacePlaceholders(
                  _get(authImageSiteCopy, 'callToAction', ''),
                  {
                    '#currentYear': new Date().getFullYear(),
                  },
                )}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    </Grid>
  );
}

AuthenticationImage.propTypes = {
  isSignUp: PropTypes.bool,
};

export default AuthenticationImage;
